import { useCallback, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Amount, Button, Panel, Select } from '@promanage/ui';
import {
  addDays,
  canOpenReconcile,
  formatNzDate,
  formatPeriod,
  periodStartFor,
} from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { ReconcileNav } from '../features/reconcile/ReconcileNav';
import { requireAccess } from '../features/auth/routeAccess';
import { DayBar } from '../features/reconcile/DayBar';
import { BankAccountChanges } from '../features/reconcile/BankAccountChanges';
import { MonthProgress } from '../features/reconcile/MonthProgress';
import { monthStatusLabel, reconciledSubtitle } from '../features/reconcile/monthState';
import { ReconciledDays } from '../features/reconcile/ReconciledDays';
import { UnreconciledList } from '../features/reconcile/UnreconciledList';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reconcile/')({
  beforeLoad: requireAccess(canOpenReconcile),
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(
      trpc.reconciliation.accounts.queryOptions(undefined, { staleTime: 10_000 }),
    );
  },
  component: ReconcilePage,
});

/**
 * Bank reconciliation, one day at a time.
 *
 * The screen has three parts and they are the operator's actual job. At the top, the DAY — the
 * cursor everything else hangs off. On the left, everything that has posted and not yet appeared
 * on the bank, ticked against the statement in hand. On the right, the days already reconciled
 * this month, each opening to what it cleared.
 *
 * ONE ENTRY PER DAY, because a bank balance moves once a day. The page opens on the day after the
 * last one reconciled, so somebody three days behind is started where they left off rather than
 * on today — and the day is a control rather than a fact, because going back over yesterday is
 * ordinary work, not an exception.
 *
 * NOTHING HERE IS PERMANENT UNTIL THE MONTH IS SIGNED OFF. A closing balance can be corrected, an
 * item ticked in error can be moved back, and both are recorded rather than erased. What bounds
 * it is the last locked month, which the server names in `earliestOpenDay` — the same pivot it
 * refuses on, so this screen cannot offer a day the server will reject.
 */
function ReconcilePage() {
  const accounts = useQuery(
    trpc.reconciliation.accounts.queryOptions(undefined, { staleTime: 10_000 }),
  );

  const [accountId, setAccountId] = useState<string | null>(null);
  /** Set only when the operator moves off the default — see below. */
  const [chosenDay, setChosenDay] = useState<string | null>(null);

  const dayBarObserver = useRef<ResizeObserver | null>(null);

  /*
   * Publish the day bar's real height, the same reason and the same technique `TopBar` measures
   * its own — `UnreconciledList`'s card is bounded to the rest of the viewport below it, and CSS
   * has no way to ask how tall a sibling is. Hand-computed arithmetic already got the top bar's
   * own height wrong twice (see `TopBar`); this is the one other piece of page chrome a panel
   * below it has to clear, so it gets the same treatment rather than a guessed constant.
   *
   * A CALLBACK ref, not `TopBar`'s `useLayoutEffect` + `useRef`. The day bar comes and goes with
   * `account && day && today` rather than mounting once with the page, so a mount-only effect
   * would measure whichever state was true the first time it ran and never again — the callback
   * fires exactly when the node itself attaches or detaches, whatever caused it.
   */
  const dayBar = useCallback((el: HTMLDivElement | null) => {
    dayBarObserver.current?.disconnect();

    if (!el) {
      // Back to the token, so a moment with no day bar cannot inherit the last one's height.
      document.documentElement.style.removeProperty('--daybar-height');
      return;
    }

    const publish = () =>
      document.documentElement.style.setProperty(
        '--daybar-height',
        `${el.getBoundingClientRect().height}px`,
      );

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    dayBarObserver.current = observer;
  }, []);

  const list = accounts.data?.accounts;
  const account = list?.find((a) => a.id === accountId) ?? list?.[0];

  /*
   * The day is DERIVED, not stored in an effect.
   *
   * Open on the day after the last one reconciled: reconciliation runs forward one day at a
   * time, and an agency three days behind should be started on the oldest of them rather than on
   * today. Computing it during render rather than writing it into state from a `useEffect` means
   * there is no frame where the page shows the wrong day, and no second source of truth to keep
   * in step when the account's last reconciled day changes underneath it.
   */
  const day =
    chosenDay ??
    (account && accounts.data
      ? clamp(
          account.lastReconciledOn
            ? addDays(account.lastReconciledOn, 1)
            : addDays(accounts.data.today, -1),
          accounts.data.earliestOpenDay,
          accounts.data.today,
        )
      : null);

  /*
   * EVERY outstanding item on the account, not the ones dated before the cursor.
   *
   * `asAt` is omitted deliberately. Bounded to the day being worked, this list hid real money
   * from the one screen whose job is finding it: an account nobody has reconciled opens on
   * yesterday, so anything receipted this morning was absent with nothing on screen to say the
   * rows existed. What a day is SIGNED OFF against is still bounded to that day — that bound
   * lives at the strike, in `unreconciledTransactions`, out of the client's reach.
   */
  const unreconciled = useQuery(
    trpc.reconciliation.unreconciled.queryOptions(
      { ledgerAccountId: account?.id ?? '' },
      {
        meta: { errorTitle: 'Could not load the unreconciled lines' },
        staleTime: 5_000,
        enabled: Boolean(account && day),
      },
    ),
  );

  /*
   * The month of the day being worked, on the account being worked.
   *
   * Not "the last 30 rows of everything". The list beside the working panel is there to be
   * corrected from, so it has to hold the days an operator might go back to — which is this
   * month on this statement, and never August's rows while somebody reconciles September.
   */
  const month = day ? periodStartFor(day) : null;

  const days = useQuery(
    trpc.reconciliation.days.queryOptions(
      { limit: 40, ledgerAccountId: account?.id ?? '', month: month ?? '' },
      {
        meta: { errorTitle: 'Could not load the reconciled days' },
        staleTime: 10_000,
        enabled: Boolean(account && month),
      },
    ),
  );

  const today = accounts.data?.today ?? null;
  const earliestOpenDay = accounts.data?.earliestOpenDay ?? null;
  const reconciled = days.data?.find((row) => row.reconciledOn === day);

  /*
   * Where the month has got to — §7's checklist, on the screen the work is done on.
   *
   * The same `ledger.monthClose` the lock dialog calls, so this can never claim a readiness the
   * lock would refuse. Read-only here: closing is a `trust_admin`'s decision and stays on
   * `/financial`. See `MonthProgress`.
   */
  const monthClose = useQuery(
    trpc.ledger.monthClose.queryOptions(
      { startsOn: month ?? '' },
      { staleTime: 30_000, enabled: Boolean(month) },
    ),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Reconcile"
        /*
         * WHERE THE ACCOUNT ACTUALLY STANDS, which is the one thing worth saying beside the
         * screen's own name — and it had a panel of its own until this took it. The gap is named
         * rather than left to be worked out from two dates: "a day behind" is what decides
         * whether the operator sits down to this now.
         *
         * `reconciledSubtitle` is the rule, in `monthState.ts`, where it is tested. It measures
         * against YESTERDAY, because a day cannot be reconciled against a closing balance the
         * bank has not struck yet.
         */
        subtitle={
          account && today
            ? reconciledSubtitle({ lastReconciledOn: account.lastReconciledOn, today })
            : 'Tick off what the bank has shown, one day at a time'
        }
        meta={
          <div className="flex items-center gap-4">
            {account ? (
              <TrustPosition
                cashBookCents={BigInt(account.cashBookCents)}
                latestBankBalanceCents={
                  account.latestBankBalanceCents === null
                    ? null
                    : BigInt(account.latestBankBalanceCents)
                }
                latestBankBalanceOn={account.latestBankBalanceOn}
              />
            ) : null}
            {/*
             * No item count here any more. "Not yet presented · 2 items" was the same fact the
             * working list states in its own footer, with the total beside it — and the footer is
             * the one an operator is actually looking at while they tick. Three figures is what
             * this row is for: the position, which no other part of the screen carries.
             */}
          </div>
        }
      />
      <ReconcileNav accountId={account?.id} />

      {account && day && today ? (
        <>
          {/*
           * §7's checklist, above the day's work rather than inside the lock dialog on another
           * screen. Read-only — see `MonthProgress` for why there is no Lock button on it.
           */}
          {/*
           * Only when something actually needs a person — unidentified money, or a break nobody
           * has explained. It renders nothing otherwise, which is most days. See `MonthProgress`.
           */}
          <MonthProgress close={monthClose.data} isPending={monthClose.isPending} />

          <div ref={dayBar}>
            <DayBar
              key={`${account.id}:${day}`}
              accountControl={
                list && list.length > 1 ? (
                  <label className="flex items-center gap-2 text-[0.75rem] text-ink-muted">
                    Account
                    <Select
                      value={account?.id ?? ''}
                      onChange={(e) => {
                        setAccountId(e.target.value);
                        // The default day belongs to the account, so a switch must fall back to it
                        // rather than carrying the previous account's position across.
                        setChosenDay(null);
                      }}
                      className="w-auto px-2 py-1 text-[0.8125rem]"
                    >
                      {list.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Select>
                  </label>
                ) : null
              }
              accountName={account.name}
              dayState={days.isPending ? 'loading' : days.isError ? 'error' : 'ready'}
              day={day}
              earliest={earliestOpenDay}
              today={today}
              ledgerAccountId={account.id}
              month={month ?? ''}
              monthStatus={monthStatusLabel(monthClose.data)}
              status={{
                reconciled: Boolean(reconciled),
                differenceCents: BigInt(reconciled?.differenceCents ?? '0'),
                critical: reconciled?.critical ?? false,
                version: reconciled?.version ?? 1,
              }}
              onChange={setChosenDay}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_24rem]">
            <UnreconciledList
              /*
               * Keyed by the day, so moving off one clears what was ticked on it.
               *
               * A tick is a claim that the bank showed this item ON THAT DAY. Carrying a
               * half-finished selection into the next day would present those items against a
               * date nobody chose them for, which is the one mistake the whole screen is built
               * to make hard.
               */
              key={`${account.id}:${day}`}
              ledgerAccountId={account.id}
              accountName={account.name}
              reconciledOn={day}
              rows={unreconciled.data ?? []}
              isPending={unreconciled.isPending || days.isPending}
              loadError={unreconciled.isError || days.isError}
              onRetry={() => {
                void unreconciled.refetch();
                void days.refetch();
              }}
              reconciled={reconciled}
            />

            <div className="flex min-w-0 flex-col gap-4">
              {/*
               * §5's last control, in the one place somebody is already looking at everything that
               * happened to the portfolio this month. It renders nothing when nothing changed —
               * see the note in the component for why an empty card would be worse than none.
               */}
              <BankAccountChanges month={periodStartFor(day)} />

              <ReconciledDays
                monthLabel={formatPeriod(periodStartFor(day))}
                days={days.data}
                isPending={days.isPending}
                error={days.error}
                onRetry={() => void days.refetch()}
              />
            </div>
          </div>
        </>
      ) : (
        <Panel className="p-4">
          <p className="text-[0.8125rem] text-ink-muted">
            {accounts.isPending
              ? 'Loading accounts…'
              : accounts.isError
                ? 'Trust bank accounts could not be loaded.'
                : 'No trust bank account yet. Add one before reconciling.'}
          </p>
          {accounts.isError ? (
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={() => void accounts.refetch()}
            >
              Try again
            </Button>
          ) : null}
        </Panel>
      )}
    </div>
  );
}

/**
 * Keep a day inside the window the server will accept.
 *
 * Both ends are real. An agency that reconciled through today should not open on tomorrow, and
 * one whose last lock is recent should not open on a day inside it — which is exactly what
 * "the day after the last reconciled day" produces after a month is signed off mid-work.
 */
function clamp(day: string, earliest: string | null, latest: string): string {
  if (earliest !== null && day < earliest) return earliest > latest ? latest : earliest;
  return day > latest ? latest : day;
}

/**
 * The trust cash book against what the bank last told us, for the account being worked.
 *
 * Not a break check — that is `ReconciliationStrip` on the dashboard, which compares figures
 * struck on the SAME date and is entitled to say balanced or not. This is the raw pair a property
 * manager glances at before starting the day's ticking: what our own ledger says right now, and
 * what the bank's own statement last said.
 *
 * NEUTRAL, NOT RED, AND THE TWO ARE MEANT TO DIFFER. The gap is everything our books hold that
 * the last statement did not show — the items still outstanding on the day it was struck, plus
 * everything posted since — which is precisely the work this screen exists to tick off. An
 * earlier version coloured it danger red the moment it was non-zero, so an account carrying one
 * unpresented receipt, or an agency that had generated a disbursement run that morning, opened
 * the screen to what read as an error. Red means error and destruction (DESIGN_SYSTEM.md §2), and
 * a figure that is non-zero on every healthy account on every ordinary day is neither. The colour
 * was doing the same thing the dashboard strip was doing: passing a verdict on a comparison
 * between two dates.
 */
function TrustPosition({
  cashBookCents,
  latestBankBalanceCents,
  latestBankBalanceOn,
}: {
  cashBookCents: bigint;
  latestBankBalanceCents: bigint | null;
  latestBankBalanceOn: string | null;
}) {
  // Absent until the account has been reconciled at least once. There is nothing to subtract
  // from, and a zero here would be a figure somebody could read as "nothing outstanding".
  const differenceCents =
    latestBankBalanceCents === null ? null : cashBookCents - latestBankBalanceCents;

  return (
    <div className="flex items-center gap-4">
      <Figure label="Trust value" value={cashBookCents} />
      {/* Dated, because it is the one figure here that is not "now" — the same reason the
          dashboard strip dates line A. */}
      <Figure
        label={latestBankBalanceOn ? `Bank, ${formatNzDate(latestBankBalanceOn)}` : 'Bank value'}
        value={latestBankBalanceCents}
      />
      {differenceCents !== null ? (
        <div className="leading-tight whitespace-nowrap">
          {/* Named for what it IS rather than "Difference", which invites the reader to treat a
              non-zero as a fault. Signed, so it is clear which way round the two sit: positive
              means our books hold more than that statement showed. */}
          <span className="block text-[0.6875rem] text-ink-subtle">Not yet on that statement</span>
          <Amount
            value={differenceCents}
            currency
            signed
            className="text-[0.9375rem] font-semibold"
          />
        </div>
      ) : null}
    </div>
  );
}

/** One figure, or a dash where the books genuinely do not have one — see `ReconciliationStrip`. */
function Figure({ label, value }: { label: string; value: bigint | null }) {
  return (
    <div className="leading-tight whitespace-nowrap">
      <span className="block text-[0.6875rem] text-ink-subtle">{label}</span>
      {value === null ? (
        <span className="text-[0.9375rem] font-semibold text-ink-subtle">—</span>
      ) : (
        <Amount value={value} currency className="text-[0.9375rem] font-semibold" />
      )}
    </div>
  );
}
